/** The seeded pen drawing shares the actor depth/composite pass. No DOM overlay
 * can cover a face, and texture decoding happens once per encounter, never per frame. */
import tgpu from 'typegpu';
import * as d from 'typegpu/data';
import * as std from 'typegpu/std';
import {frameLayout,illustrationLayout} from '../gpu/layouts';
import {rnd2,vnoise} from './lib';

export const illustrationFragment=tgpu.fragmentFn({in:{fragPos:d.builtin.position},out:d.vec4f})((input)=>{
 'use gpu';
 const sl=frameLayout.$.light;
 const rect=sl.panelRectPx;
 const uv=input.fragPos.xy.sub(rect.xy).div(rect.zw);
 let col=std.textureSampleLevel(illustrationLayout.$.page,illustrationLayout.$.samp,uv,0).rgb;
 // Contact ink follows posed feet/body patches in world space. The background
 // has no 3D floor, so project the real contacts into its miniature plane.
 const f=frameLayout.$.frame;
 for(let i=d.u32(0);i<sl.qualityFlags.y;i++){
  const c=sl.contact[i];
  const clip=f.viewProj.mul(d.vec4f(c.x,0,c.y,1));
  const edge=f.viewProj.mul(d.vec4f(c.x+c.w,0,c.y,1));
  const ndc=clip.xy.div(clip.w);
  const center=d.vec2f(ndc.x*.5+.5,.5-ndc.y*.5).mul(f.viewport.xy);
  const radius=std.max(2,std.abs(edge.x/edge.w-ndc.x)*f.viewport.x*.5);
  const q=input.fragPos.xy.sub(center).div(d.vec2f(radius,std.max(1.5,radius*.20)));
  const rough=vnoise(input.fragPos.xy.mul(.3))*.12;
  const stamp=(1-std.smoothstep(.67,.97,std.length(q)+rough))/(1+std.max(c.z,0)*18);
  const lines=.18+(1-std.smoothstep(.10,.28,std.abs(std.fract(input.fragPos.y*.43+q.x*.12)-.5)))*.45;
  col=std.mix(col,d.vec3f(.055,.032,.017),stamp*lines);
 }
 // Both figures and the painted page pass through the same paper/ink composite.
 // Punctuated weather is a restrained pen accent, not a translucent fog sheet.
 if(sl.expedition.y!==11&&sl.expedition.z===1){
  const p=uv.mul(d.vec2f(63,16)).add(d.vec2f(uv.y*7,-sl.timing.x*.55));
  const seed=rnd2(std.floor(d.vec2f(p.x,sl.expedition.x)));
  const dash=std.smoothstep(.78,.84,std.fract(p.y+seed*13))*(1-std.smoothstep(.94,.99,std.fract(p.y+seed*13)));
  const line=1-std.smoothstep(.02,.08,std.abs(std.fract(p.x)-.5));
  col=std.mix(col,d.vec3f(.70,.59,.37),line*dash*.1);
 }
 const pigment=vnoise(uv.mul(d.vec2f(137,121)));
 col=col.mul(.975+pigment*.05);
 return d.vec4f(col,0);
});
